/**
 * The dev-tools switch for the pricing rate breakdown.
 *
 * Two halves, and the split is the point:
 *
 * 1. **The request header.** When on, the API client sends
 *    `x-butter-dev-tools: 1` and the server includes the rate build-up in the
 *    quote. When off it sends nothing and the server returns nothing — so a
 *    production payload never carries a club's pricing structure, rather than
 *    carrying it hidden behind a client-side `if`.
 * 2. **The last quote seen**, recorded so the tool has something to render
 *    without re-quoting.
 *
 * Read synchronously from `localStorage` because `buildHeaders` is synchronous;
 * the rest of dev-tools storage is async over `browserAdapter` and cannot be
 * used here.
 *
 * Every entry point is inert unless `SHOW_DEV_TOOLS`, so none of this can be
 * switched on in production by writing a storage key.
 *
 * **Why the gate is computed here instead of imported from `~/config/env`.**
 * This module is reached from `lib/apiClient`'s header builder, so it loads in
 * every unit test that touches the API client. `~/config/env` validates the
 * whole environment at import time and throws when `VITE_API_BASE_URL` is
 * absent — which is every CI run, where no `.env` exists. Reading
 * `import.meta.env` directly keeps this module free of that side effect. The
 * expression is the same one `SHOW_DEV_TOOLS` uses; keep the two in step.
 *
 * @module features/dev-tools/rateBreakdown
 */

import type { ApiFeeItem } from '@lgc/database/types'

const SHOW_DEV_TOOLS =
  import.meta.env.VITE_ENABLE_DEV_TOOLS === 'true' &&
  (import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === 'staging')

const STORAGE_KEY = 'devtools:rate_breakdown'

/** Whether the client should ask the server for the rate build-up. */
export function isRateBreakdownEnabled(): boolean {
  if (!SHOW_DEV_TOOLS) return false
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    // Private browsing, blocked storage — treat as off rather than throwing
    // inside a header builder on every request.
    return false
  }
}

export function setRateBreakdownEnabled(enabled: boolean): void {
  if (!SHOW_DEV_TOOLS) return
  try {
    if (enabled) localStorage.setItem(STORAGE_KEY, '1')
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do — the toggle simply will not persist.
  }
}

/** The most recent quote's fees, kept only so the tool can render them. */
let lastQuotedFees: ApiFeeItem[] | null = null

export function recordQuotedFees(fees: ApiFeeItem[] | undefined | null): void {
  if (!SHOW_DEV_TOOLS) return
  lastQuotedFees = fees ?? null
}

export function getLastQuotedFees(): ApiFeeItem[] | null {
  return SHOW_DEV_TOOLS ? lastQuotedFees : null
}
